import * as turf from '@turf/turf';
import { compact, isEqual, uniqBy } from 'lodash';
import moment from 'moment';
import { Types } from 'mongoose';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { useQueryClient } from 'react-query';
import { normalizeVenueFromSource, useCountrySources } from 'sonata-platform-components';
import { showNotification, useQueryFetch } from 'sonata-react-components';
import {
  createEntityZones,
  getCartographyLevels,
  getEntityZones,
  getInfoFromMapBox,
  getScreens,
  getVenues,
  getVenuesInfoFromMapBox,
  saveScreens,
  syncRelationsVenues,
  updateEntityZones,
  updateVenues,
  ZoneEntity
} from 'sonata-static-storage/lib/entities';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';
import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';
import { ItemsGroupsDefaultConfigurations } from 'sonata-static-storage/lib/samples/itemsGrouping.samples';
import { type SourceType, Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import { enableAttributionLocations } from '@/helpers/common/attribution.helper';
import { Token } from '@/services/common.types';
import { postAddVenues } from '@/services/venue.service';
import { getSaveStepTotal } from '@/types/saveSteps.types';
import { getSourceQueryFields } from '../../../services/sources.service';
import { putUser } from '../../../services/user.service';
import { reloadUser } from '../../../store/global/user/selectors';
import {
  findCountryFeature,
  fitBoundsOptions,
  resolveLocationInfo,
  sanitizeObj
} from './components/MapView/map.helpers';
import { MapMode, MapShape, ModifiedScreen, ModifiedVenue, ModifiedZone } from './components/MapView/map.types';
import { DEFAULT_RADIUS_IN_KM } from './constants';
import { ensureDefaultScreenGroup } from './grouping/domain/familyAdapters';
import { collectGroupIds, createGroupId } from './grouping/domain/groupIds';
import { initialGroupIds } from './grouping/domain/groupQueries';
import { mergeVenueGroupsForSave } from './grouping/domain/venueGroupsPersistence';
import {
  buildEnabledAttributionSettings,
  buildEnabledFootfallAttributionSettings,
  countSaveActions,
  createTorusRingFeature,
  generateRandomColor,
  getAttributionZonesObject,
  getCommittedAttributionType,
  getFormattedDateByUser,
  getVenueEntityRelationKey,
  getVenueIdsToRemove,
  needsAttributionSettingsUpdate,
  sumSaveStepCounts
} from './MultiFlightMap.helpers';
import { useProcessCSVFile } from './MultiFlightMap.hooks';

import type { Feature, GeoJsonProperties, Geometry } from 'geojson';
import type { PropsWithChildren } from 'react';
import type { AttributionSettings } from 'sonata-static-storage/lib/schemas/campaign/attributionSettings.schema';
import type { ScreenGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type { ZoneGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/zoneGroup.schema';
import type { ProcessUploadZone } from 'sonata-static-storage/lib/schemas/processUpload/processUploadZone.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type {
  AddVenuesOptions,
  AttributionEnableMode,
  AttributionType,
  AttributionZones,
  CircleZone,
  Context,
  ExportFromSonataLIData,
  ExtendedShape,
  ListType,
  ProviderProps,
  VenueGroupExtended,
  ZoneFeature
} from './multiFlightMap.types';

const MultiFlightMapContext = createContext<Context | null>(null);

type State = {
  attributionZones: AttributionZones;
  cursorPosition: { latitude: number; longitude: number } | null;
  editedCircleId: string | null;
  editedPolygonId: string | null;
  isSaving: boolean;
  mode: MapMode;
  selectedItemId: string | null;
  selectedItems: Record<ListType, string[]>;
  temporaryLocation: (Partial<Zone> & Pick<ExtendedShape, 'boundingBox'>) | null;
  temporaryScreen: GeoDataAndScreensInteface | null;
  temporaryScreens: Partial<ModifiedScreen>[];
  temporaryScreenGroups: ScreenGroupInterface[];
  temporaryVenueGroups: VenueGroupExtended[];
  temporaryVenues: Partial<ModifiedVenue>[];
  temporaryZones: Partial<ModifiedZone>[];
};

function calculateCircleArea(radius: number): number {
  return Math.round(Math.PI * radius ** 2);
}

async function generateZoneEntity({ address, center, groups, name: nameOverride, radiusInKm, venueId }: CircleZone) {
  const radiusInMeters = radiusInKm * 1000;

  const { country: resolvedCountry, placeName } = await resolveLocationInfo(center[1], center[0]);

  const name = nameOverride || placeName || `New area ${moment().format('YYYY-MM-DD hh:mm:ss')}`;
  const country = resolvedCountry || 'N/A';

  const id = new Types.ObjectId().toString();

  const circleZone: Partial<ModifiedZone> = {
    ...(address && { address }),
    _id: id,
    area: calculateCircleArea(radiusInMeters),
    country,
    groups,
    isModified: true,
    latlng: {
      lat: center[1],
      lng: center[0]
    },
    name,
    radius: radiusInMeters,
    ...(venueId && { shop: venueId }),
    type: 'circle',
    visible: true
  };

  const zoneEntity = new ZoneEntity(circleZone);

  return structuredClone(zoneEntity.entity);
}

function MultiFlightMapProvider({
  children,
  list,
  mapId,
  setList,
  ...contextData
}: PropsWithChildren<ProviderProps>): React.ReactElement {
  const tokens: Record<string, Token> = {
    venuesBatchSave: {}
  };
  const queryClient = useQueryClient();
  const { [mapId]: map } = useMap();
  const { formatMessage } = useIntl();
  const {
    currentSection,
    onChangeScreenGroups,
    onChangeZoneGroups,
    onUnsavedMapChangesChange,
    onRefreshUser,
    onVenuesChange,
    screenGroups,
    venues: contextDataVenues,
    zoneGroups,
    zones: contextDataZones = []
  } = contextData;

  const [
    {
      attributionZones,
      cursorPosition,
      editedCircleId,
      editedPolygonId,
      isSaving,
      mode,
      selectedItemId,
      selectedItems,
      temporaryLocation,
      temporaryScreen,
      temporaryScreenGroups,
      temporaryScreens,
      temporaryVenueGroups,
      temporaryVenues,
      temporaryZones
    },
    setState
  ] = useState<State>({
    attributionZones: {},
    cursorPosition: null,
    editedCircleId: null,
    editedPolygonId: null,
    isSaving: false,
    mode: null,
    selectedItemId: null,
    selectedItems: {
      locations: [],
      screens: [],
      venues: []
    },
    temporaryLocation: null,
    temporaryScreen: null,
    temporaryScreenGroups: [],
    temporaryScreens: [],
    temporaryVenueGroups: [],
    temporaryVenues: contextDataVenues || [],
    temporaryZones: []
  });

  // Groups present when the map loaded (refreshed after each save): deleting one of them must be a
  // soft delete, while groups created in this session can simply be dropped.
  const [groupsExistingOnLoad, setGroupsExistingOnLoad] = useState<Record<ListType, ReadonlySet<string>>>(() => ({
    locations: collectGroupIds(zoneGroups),
    screens: collectGroupIds(screenGroups),
    venues: collectGroupIds(contextData.user?.venueGroups)
  }));

  const newItems = useRef<{
    venues: Set<string>;
    zones: Set<string>;
  }>({
    venues: new Set(),
    zones: new Set()
  });

  const normalizedUser = {
    ...contextData.user,
    _id: contextData.user._id || contextData.user.id
  };

  const { screens: onChangeScreens, zones: onChangeZones } = contextData.onChangeItems ?? {};

  const zoneNotifier = (zones: Zone[]) => {
    const zonesToNotify = zones.filter(zone => !zone.deleted);
    onChangeZones?.(zonesToNotify);
  };

  const screensNotifier = (screens: Partial<ModifiedScreen>[]) => {
    const screensToNotify = screens.filter(screen => !screen.deleted);
    onChangeScreens?.(screensToNotify);
  };

  if (!isEqual(contextDataVenues, temporaryVenues)) {
    contextDataVenues.forEach(venue => {
      if ((venue as Record<string, any>).isModified) {
        newItems.current.venues.add(venue._id!);
      }
    });
    setState(prevState => ({
      ...prevState,
      temporaryVenues: contextDataVenues || []
    }));
  }

  if (Array.isArray(contextDataZones) && contextDataZones.length) {
    const locallyDeletedZones = temporaryZones.filter(zone => zone.deleted && zone.isModified);
    const locallyDeletedIds = new Set(locallyDeletedZones.map(zone => zone._id));
    const mergedZones = [...contextDataZones.filter(zone => !locallyDeletedIds.has(zone._id)), ...locallyDeletedZones];
    if (!isEqual(mergedZones, temporaryZones)) {
      setState(prevState => ({
        ...prevState,
        temporaryZones: mergedZones
      }));
    }
  }

  const {
    data: countryData,
    isFetching: countryDataFetching,
    isLoading: countryDataLoading
  } = useQueryFetch(
    ['multiFlightMap-country-boundaryBox', contextData.countries[0]],
    async () => {
      const data = await getCartographyLevels(contextData.countries[0], 'level0', { fields: ['boundingBox'] });
      return data.length > 0 ? data[0].boundingBox : null;
    },
    {
      cacheTime: 0,
      enabled: contextData.countries.length > 0,
      staleTime: 0
    }
  );

  const screensQueryFields = [
    'allowed_ad_types',
    'adExchange',
    'external_deals',
    'resolution',
    'publisher',
    'aspect_ratio'
  ];
  const { data: screensSourceQueryFields } = useQueryFetch(
    ['getScreensSourceDictionary'],
    async () => getSourceQueryFields('screens', screensQueryFields),
    {
      enabled: contextData.type === 'dooh'
    }
  );

  const getCartographyZoneCountryLevelId = (zone: Partial<Zone>): string => {
    return `${zone.country!}_${zone.cartographyLevel!}`;
  };

  const centerElementsOnTheMap = (zones: Partial<ModifiedZone>[] = temporaryZones) => {
    const features = zones
      .filter(z => !z.deleted)
      .reduce((prev: (ZoneFeature | null)[], zone: Partial<ModifiedZone>) => {
        if ((zone.type === 'polygon' || zone.type === 'geojson') && zone?.boundingBox) {
          return [...prev, turf.bboxPolygon(zone.boundingBox) as ZoneFeature];
        }
        if ((zone.type === 'polygon' || zone.type === 'geojson') && zone.geojson) {
          return [...prev, zone.geojson as unknown as ZoneFeature];
        }
        if (zone.type === 'circle' || zone.type === 'torus') {
          return [
            ...prev,
            turf.circle([zone.latlng!.lng, zone.latlng!.lat], zone.radius! / 1000, {
              steps: 64,
              units: 'kilometers'
            }) as ZoneFeature
          ];
        }
        if (zone.type === 'attributionTorus' && Array.isArray(zone.torusConfig) && zone.latlng) {
          const configs = [...zone.torusConfig]
            .filter(cfg => !cfg.deleted && typeof cfg.radius === 'number')
            .sort((a, b) => b.radius - a.radius);

          const center: [number, number] = [zone.latlng.lng, zone.latlng.lat];
          const torusFeatures = configs.map((cfg, idx): ZoneFeature | null => {
            const outerRadius = cfg.radius;
            const innerRadius = configs[idx + 1]?.radius ?? 0;
            return createTorusRingFeature(center, outerRadius, innerRadius, {
              ...zone,
              torusConfigId: cfg.id
            });
          });
          return [...prev, ...torusFeatures];
        }
        return [...prev, null];
      }, []);
    const validFeatures = features.filter((f): f is ZoneFeature => f !== null);
    const pointFeatures = [
      ...temporaryVenues.map(v => ({
        deleted: v.deleted,
        lat: v.latlng?.lat as number,
        lng: v.latlng?.lng as number
      })),
      ...temporaryScreens.map(s => ({
        deleted: s.deleted,
        lat: s.latitude as number,
        lng: s.longitude as number
      }))
    ]
      .filter(p => !p.deleted && p.lat !== undefined && p.lng !== undefined)
      .map(p => turf.point([p.lng!, p.lat!], {} as GeoJsonProperties));
    if (pointFeatures?.length === 0 && validFeatures?.length === 0) {
      return;
    }
    const fc = turf.featureCollection<Geometry, GeoJsonProperties>([...validFeatures, ...pointFeatures]);
    const bbox = turf.bbox(fc);

    map?.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]]
      ],
      {
        ...fitBoundsOptions,
        padding: 100
      }
    );
  };

  const { data: shapesData, isLoading: isLoadingShapes } = useQueryFetch(
    ['multiFlightMap-entities', contextData.entityId, contextData.entityType],
    async () => {
      const zones = await getEntityZones(contextData.entityType, contextData.entityId);
      const cartographyZones = zones.filter(zone => zone.country && zone.cartographyLevelId && zone.cartographyLevel);
      const groupedZones: Record<string, { country: string; ids: string[]; level: string }> = cartographyZones.reduce(
        (acc, zone) => ({
          ...acc,
          [getCartographyZoneCountryLevelId(zone)]: {
            ...acc[getCartographyZoneCountryLevelId(zone)],
            country: zone.country!,
            ids: [...(acc[getCartographyZoneCountryLevelId(zone)]?.ids || ([] as string[])), zone.cartographyLevelId!],
            level: zone.cartographyLevel!
          }
        }),
        {} as Record<string, { country: string; ids: string[]; level: string }>
      );
      const boundingBoxesDictionary = await Object.values(groupedZones).reduce(
        async (acc: Promise<Record<string, [number, number, number, number]>>, group) => {
          const zonesBoundingBoxes = await getCartographyLevels(group.country, group.level, {
            fields: ['boundingBox', 'id'],
            filters: { [group.level]: group.ids }
          });
          return {
            ...(await acc),
            ...zonesBoundingBoxes.reduce(
              (dict, shape) => ({
                ...dict,
                [shape.name]: shape.boundingBox!
              }),
              {} as Record<string, [number, number, number, number]>
            )
          };
        },
        {} as Promise<Record<string, [number, number, number, number]>>
      );

      const initialAttributionZones = getAttributionZonesObject(zones);
      const initialTorusZones = zones
        .filter(({ type }) => type === 'torus')
        .flatMap(zone =>
          (zone.torusConfig || []).map((cfg: Zone['torusConfig'][number]) => ({
            ...zone,
            parentTorus: zone._id,
            ...cfg,
            _id: cfg.id,
            area: calculateCircleArea(cfg.radius)
          }))
        );

      const initialZones = [
        ...zones
          .filter(({ type }) => !['attributionTorus', 'torus'].includes(type))
          .map(zone => ({
            ...zone,
            boundingBox: boundingBoxesDictionary[zone.cartographyLevelId!],
            isModified: false
          })),
        ...initialTorusZones
      ];
      centerElementsOnTheMap(initialZones);

      setState(prevState => ({
        ...prevState,
        attributionZones: initialAttributionZones,
        temporaryZones: initialZones
      }));
      zoneNotifier(initialZones);
      return initialZones;
    },
    {
      cacheTime: 0,
      enabled: Boolean(contextData.countries.length > 0 && temporaryZones.length === 0 && map)
    }
  );

  const { data: screensData, isLoading: isLoadingScreens } = useQueryFetch(
    ['multiFlightMap_screens', contextData.entityId, contextData.entityType, contextData.user._id],
    async () => {
      try {
        const screens = await getScreens(contextData.entityType, contextData.entityId, contextData.countries);

        if (screens.items.length > 0 && map) {
          const validScreens = screens.items.filter(
            screen => screen.latitude !== undefined && screen.longitude !== undefined && !screen.deleted
          );

          if (validScreens.length === 1) {
            const screen = validScreens[0];
            map.flyTo({
              center: [screen.longitude!, screen.latitude!],
              curve: 0.8,
              speed: 3,
              zoom: 16
            });
          } else if (validScreens.length > 1) {
            const bounds = validScreens.reduce(
              (internalBounds, screen) => {
                return [
                  [Math.min(internalBounds[0][0], screen.longitude!), Math.min(internalBounds[0][1], screen.latitude!)],
                  [Math.max(internalBounds[1][0], screen.longitude!), Math.max(internalBounds[1][1], screen.latitude!)]
                ];
              },
              [
                [validScreens[0].longitude!, validScreens[0].latitude!],
                [validScreens[0].longitude!, validScreens[0].latitude!]
              ]
            ) as [[number, number], [number, number]];

            map.fitBounds(bounds, {
              ...fitBoundsOptions,
              padding: 100
            });
          }
        }
        setState(prevState => ({
          ...prevState,
          temporaryScreens: screens.items
        }));
        screensNotifier(screens.items);
        return screens.items;
      } catch {
        return [];
      }
    },
    {
      cacheTime: 0,
      enabled: Boolean(contextData.countries && contextData.type === 'dooh' && temporaryScreens.length === 0 && map),
      staleTime: 0
    }
  );

  useEffect(() => {
    if (map && countryData && !isLoadingShapes && !isLoadingScreens && !shapesData?.length && !screensData?.length) {
      map.fitBounds(countryData, fitBoundsOptions);
    }
  }, [countryData, isLoadingShapes, isLoadingScreens, map, shapesData, screensData]);

  useEffect(() => {
    return () => {
      Object.keys(tokens).forEach(key => {
        tokens[key]?.cancel?.();
      });
    };
  }, []);

  const { data: venuesData, isLoading: isLoadingVenues } = useQueryFetch(
    ['multiFlightMap-venues', contextData.entityId, contextData.entityType, contextData.user._id],
    async () => {
      const venues = await getVenues(contextData.entityId, contextData.entityType);
      setState(prevState => ({
        ...prevState,
        temporaryVenues: venues.items
      }));
      onVenuesChange(venues.items);
      return venues.items;
    },
    {
      cacheTime: 0,
      enabled: temporaryVenues.length === 0,
      staleTime: 0
    }
  );

  // What `onSave` below will persist, split by the save step that persists it
  // and by what the save will do with each item. The predicates here are the
  // ones `onSave` picks its create/update batches with, so what the tooltip
  // promises and what actually runs cannot drift apart.
  const venueIdsToRemove = getVenueIdsToRemove(
    temporaryVenues,
    venuesData,
    contextData.entityType,
    contextData.entityId
  );
  const removedVenueIds = new Set(venueIdsToRemove);
  const unsavedVenuesCounts = countSaveActions(temporaryVenues, {
    isNew: venue => newItems.current.venues.has(venue._id!),
    isRemoved: venue => removedVenueIds.has(venue._id!)
  });
  const hasUnsavedVenues = getSaveStepTotal(unsavedVenuesCounts) > 0 || temporaryVenueGroups.length > 0;
  // Attribution torus rings are filtered out of `temporaryZones` by type and
  // live in `attributionZones` instead, so they need counting of their own —
  // even though `onSave` batches them into the same requests as the areas.
  const unsavedAttributionZonesCounts = countSaveActions(Object.values(attributionZones), {
    isNew: zone => newItems.current.zones.has(zone._id!)
  });
  const hasUnsavedAttributionZones = getSaveStepTotal(unsavedAttributionZonesCounts) > 0;
  // Screens have no new-item set of their own: a modified screen absent from
  // the fetched ones is something this session added. Their batch only carries
  // modified screens, hence the flag in `isNew` too.
  const savedScreenIds = new Set((screensData ?? []).map(screen => String(screen._id)));
  const unsavedAreasCounts = sumSaveStepCounts(
    countSaveActions(temporaryZones, { isNew: zone => newItems.current.zones.has(zone._id!) }),
    countSaveActions(temporaryScreens, {
      isNew: screen => Boolean(screen.isModified) && !savedScreenIds.has(String(screen._id)),
      isRemoved: screen => Boolean(screen.deleted || screen.removed)
    })
  );
  const hasUnsavedAreas = getSaveStepTotal(unsavedAreasCounts) > 0;
  // Screen groups are not part of the areas step: they live on the entity
  // (`screenGroups`) and travel with the entity save. They still count as
  // unsaved map changes for the navigation guard.
  const hasUnsavedMapChanges =
    hasUnsavedVenues || hasUnsavedAttributionZones || hasUnsavedAreas || temporaryScreenGroups.length > 0;

  useEffect(() => {
    onUnsavedMapChangesChange(hasUnsavedMapChanges);
  }, [hasUnsavedMapChanges]);

  // Entities without an active "UnGrouped" default group (legacy ones) get one, written straight to the entity's
  // `screenGroups` (not to `temporaryScreenGroups`), so it travels with the entity save without flagging the map
  // as having unsaved changes. Screens are not touched here: they join it on the next group change.
  useEffect(() => {
    if (!screenGroups) {
      return;
    }
    const withDefault = ensureDefaultScreenGroup(screenGroups);
    if (withDefault !== screenGroups) {
      onChangeScreenGroups(withDefault);
    }
  }, [screenGroups]);

  useEffect(() => {
    contextData.onPendingSaveStepsChange?.({
      attributionZones: {
        counts: unsavedAttributionZonesCounts,
        status: hasUnsavedAttributionZones ? 'pending' : 'disabled'
      },
      screens: { counts: unsavedAreasCounts, status: hasUnsavedAreas ? 'pending' : 'disabled' },
      venues: { counts: unsavedVenuesCounts, status: hasUnsavedVenues ? 'pending' : 'disabled' }
    });
  }, [
    hasUnsavedVenues,
    getSaveStepTotal(unsavedVenuesCounts),
    hasUnsavedAttributionZones,
    getSaveStepTotal(unsavedAttributionZonesCounts),
    hasUnsavedAreas,
    getSaveStepTotal(unsavedAreasCounts)
  ]);

  const { data: sourcesByCountry, isLoading: isLoadingSources } = useCountrySources(
    contextData.countries[0],
    contextData.user._id || contextData.user.id,
    contextData.entityId
  );

  const allShapes = [
    ...(isLoadingShapes
      ? []
      : (temporaryZones || [])
          .filter((zone: Partial<Zone>) => {
            const geojson = zone.geojson as any;
            return (
              zone.type === 'polygon' &&
              (geojson?.geometry?.type === 'Polygon' || geojson?.geometry?.type === 'MultiPolygon') &&
              Array.isArray(geojson.geometry.coordinates) &&
              !zone.deleted
            );
          })
          .map((zone: Partial<Zone>): MapShape => {
            const geojson = zone.geojson as any;
            const base = {
              id: zone._id || `zone-${Date.now()}`,
              smart: zone.smart,
              visible: zone.visible
            };
            return geojson.geometry.type === 'MultiPolygon'
              ? { ...base, coordinates: geojson.geometry.coordinates, type: 'MultiPolygon' }
              : { ...base, coordinates: geojson.geometry.coordinates, type: 'Polygon' };
          }))
  ];

  const allLocations = [
    ...(isLoadingShapes
      ? []
      : (temporaryZones || []).filter((zone: Partial<Zone>) => {
          return zone.type === 'geojson' && !zone.deleted;
        }))
  ];

  const circleZones = [
    ...(isLoadingShapes
      ? []
      : (temporaryZones || []).filter((zone: Partial<Zone>) => {
          return zone.type === 'circle' && !zone.deleted;
        }))
  ];

  const torusZones = [
    ...(isLoadingShapes
      ? []
      : (temporaryZones || []).filter((zone: Partial<Zone>) => {
          return zone.type === 'torus' && !zone.deleted;
        }))
  ];

  const setSelectedItemId = (id: string | null) => {
    setState(prevState => ({
      ...prevState,
      selectedItemId: id
    }));
  };

  const setSelectedItems = (itemType: ListType, ids: string[]) => {
    setState(prevState => ({
      ...prevState,
      selectedItems: {
        ...prevState.selectedItems,
        [itemType]: ids
      }
    }));
  };

  // Pure setter: orphan pruning is owned by `useEffect([temporaryVenues])`
  // so it runs after companion `updateVenue` calls — otherwise a freshly
  // minted group can be pruned before its venues land.
  const setTemporaryVenueGroups = (
    nextOrUpdater: VenueGroupExtended[] | ((prev: VenueGroupExtended[]) => VenueGroupExtended[])
  ) => {
    setState(prevState => {
      const candidate =
        typeof nextOrUpdater === 'function'
          ? (nextOrUpdater as (prev: VenueGroupExtended[]) => VenueGroupExtended[])(prevState.temporaryVenueGroups)
          : nextOrUpdater;
      const cleaned = candidate.filter(g => Boolean(g._id));
      return {
        ...prevState,
        temporaryVenueGroups: cleaned
      };
    });
  };

  // Same pure-setter rule as `setTemporaryVenueGroups`. Callers must mint
  // new groups with `groupType: 'custom'`; system-managed types live on
  // the entity's canonical `screenGroups` prop and are not editable here.
  const setTemporaryScreenGroups = (
    nextOrUpdater: ScreenGroupInterface[] | ((prev: ScreenGroupInterface[]) => ScreenGroupInterface[])
  ) => {
    setState(prevState => {
      const candidate =
        typeof nextOrUpdater === 'function'
          ? (nextOrUpdater as (prev: ScreenGroupInterface[]) => ScreenGroupInterface[])(prevState.temporaryScreenGroups)
          : nextOrUpdater;
      const cleaned = candidate.filter(g => Boolean(g._id));
      return {
        ...prevState,
        temporaryScreenGroups: cleaned
      };
    });
  };

  const setMode = (newMode: MapMode) => {
    setState(prevState => {
      if (prevState.mode === newMode) {
        return prevState;
      }
      return {
        ...prevState,
        ...(!['edit-circle', 'edit-polygon'].includes(newMode ?? '')
          ? {
              editedCircleId: null,
              editedPolygonId: null
            }
          : {}),
        cursorPosition: newMode !== 'add-venues' ? null : prevState.cursorPosition,
        mode: newMode
      };
    });
  };

  const setEditedPolygonId = (id: string) => {
    setState(prevState => ({
      ...prevState,
      editedCircleId: null,
      editedPolygonId: id,
      mode: 'edit-polygon'
    }));
  };

  const setEditedCircleId = (id: string) => {
    setState(prevState => ({
      ...prevState,
      editedCircleId: id,
      editedPolygonId: null,
      mode: 'edit-circle'
    }));
  };

  const clearTemporaryItems = () => {
    setState(prevState => ({
      ...prevState,
      temporaryLocation: null,
      temporaryScreen: null
    }));
    zoneNotifier(temporaryZones as Zone[]);
  };

  const setListAndState = (
    newList: ListType,
    ...updates: (Partial<State> | ((prevState: State) => Partial<State>))[]
  ) => {
    setList(newList);
    if (updates.length > 0) {
      setState((prevState: State) => {
        return updates.reduce<Partial<State>>((acc, update) => {
          if (typeof update === 'function') {
            return { ...acc, ...update(acc as State) };
          }
          return { ...acc, ...update };
        }, prevState) as State;
      });
    }
  };

  const addVenue = async (lat: number, lng: number, name: string) => {
    if (getCommittedAttributionType(temporaryVenues) === 'driveToStore') {
      return;
    }
    const venueInfo = await getVenuesInfoFromMapBox(
      contextData.entityId,
      contextData.entityType,
      lat,
      lng,
      normalizedUser._id as string,
      name,
      'myVenues' // Change logic if you add venue from PoiBrands or OSM
    );
    const newVenues = [...temporaryVenues, venueInfo];

    newItems.current.venues.add(venueInfo._id!);
    setListAndState('venues', {
      mode: null,
      temporaryVenues: newVenues
    });
    onVenuesChange(newVenues as Venue<string>[]);

    createAttributionZonesForVenues([venueInfo], 'footfall');
    enableAttributionSettingsIfNeeded('footfall');
  };

  const handleAddBatchVenues = async (batchVenues: Partial<Venue>[], options?: AddVenuesOptions) => {
    await onRefreshUser();
    const mergedVenues = compact(uniqBy(batchVenues, venue => JSON.stringify(venue)));
    let venues: ModifiedVenue[] = [];
    venues = mergedVenues.map(venue => ({
      ...(contextData.entityType === 'lineItem'
        ? { campaigns: [contextData.entityId] as any }
        : { insertionOrders: [contextData.entityId] as any }),
      ...(venue?.geoDataId && { geoDataId: venue.geoDataId }),
      ...(venue?.geoDataSourceId && { geoDataSourceId: venue.geoDataSourceId }),
      ...(venue?.country && { country: venue.country }),
      ...(venue?.tags && { tags: venue.tags }),
      _id: new Types.ObjectId().toString(),
      groups: venue?.groups || [],
      isModified: true,
      latlng: {
        lat: venue?.latlng!.lat,
        lng: venue?.latlng!.lng
      },
      name: venue.name,
      sourceType: venue.sourceType || 'myVenues',
      user: contextData.user._id || (contextData.user.id as any)
    }));
    const newVenues = [...temporaryVenues, ...venues];
    venues.forEach(venue => {
      newItems.current.venues.add(venue._id!);
    });
    setListAndState('venues', {
      mode: null,
      temporaryVenues: newVenues
    });
    onVenuesChange(newVenues as Venue<string>[]);

    if (options?.attribution) {
      createAttributionZonesForVenues(venues, options.attributionMode, options.attributionRadiusInMeters);
      enableAttributionSettingsIfNeeded(options.attributionMode);
    }
    return newVenues as Venue<string>[];
  };

  const addSourcesAsVenues = async (sources: Record<string, any>[], sourceId: string, options?: AddVenuesOptions) => {
    const normalizedVenues = normalizeVenueFromSource({
      entityId: contextData.entityId,
      entityType: contextData.entityType,
      sourceId,
      userId: contextData.user._id || (contextData.user.id as any),
      venues: sources
    });
    const newVenues = [...temporaryVenues, ...normalizedVenues];
    normalizedVenues.forEach((venue: Venue<string>) => {
      newItems.current.venues.add(venue._id!);
    });
    setListAndState('venues', {
      mode: null,
      temporaryVenues: newVenues
    });
    onVenuesChange(newVenues as Venue<string>[]);

    if (options?.attribution) {
      createAttributionZonesForVenues(normalizedVenues, options.attributionMode, options.attributionRadiusInMeters);
      enableAttributionSettingsIfNeeded(options.attributionMode);
    }
    return normalizedVenues as Venue<string>[];
  };

  const addSourcesAsScreens = (items: Record<string, any>[]) => {
    const currentTemporaryScreens = temporaryScreens.filter(s => !s.deleted).map(screen => screen.source_element_id);

    const nonDuplicatedItems = items
      .filter(item => !currentTemporaryScreens.includes(item.id))
      .map(item => ({
        ...item,
        _id: new Types.ObjectId().toString(),
        isModified: true,
        ...(contextData.type === 'dooh' &&
          screensQueryFields.reduce(
            (acc, queryField) => {
              const queryFieldData = screensSourceQueryFields?.find(({ name }) => name === queryField);
              if (!queryFieldData) {
                return acc;
              }
              if (Array.isArray(item[queryField])) {
                return {
                  ...acc,
                  [queryField]:
                    item[queryField]?.map(({ id }: { id: number }) => ({
                      id,
                      name: queryFieldData.optionsDictionary[id]
                    })) || []
                };
              }
              if (typeof item[queryField] === 'object' && item[queryField] !== null) {
                return {
                  ...acc,
                  [queryField]: item[queryField]
                };
              }
              return {
                ...acc,
                [queryField]: {
                  id: item[queryField] as number,
                  name: queryFieldData.optionsDictionary[item[queryField] as number]
                }
              };
            },
            {} as Record<string, any>
          )),
        groups: initialGroupIds('screens', screenGroups ?? []),
        source_element_id: item.id,
        tags: item.tags,
        type: 'screens'
      }));
    const newTemporaryScreens = [...temporaryScreens, ...nonDuplicatedItems] as GeoDataAndScreensInteface[];
    setListAndState('screens', {
      mode: null,
      temporaryScreen: null,
      temporaryScreens: newTemporaryScreens
    });
    screensNotifier(newTemporaryScreens);
  };

  const updateVenue = async (updatedVenue: Partial<Venue> | Partial<Venue>[]) => {
    const updatedVenuesArray = Array.isArray(updatedVenue) ? updatedVenue : [updatedVenue];
    const updatedVenues = temporaryVenues.map(venue => {
      const matchingVenue = updatedVenuesArray.find(updated => updated._id === venue._id);
      return matchingVenue
        ? {
            ...venue,
            ...matchingVenue,
            isModified: true
          }
        : venue;
    });
    setState(prevState => ({
      ...prevState,
      temporaryVenues: updatedVenues
    }));
    onVenuesChange(updatedVenues as Venue<string>[]);
  };

  const updateScreen = async (updatedScreen: Partial<ModifiedScreen> | Partial<ModifiedScreen>[]) => {
    const updatedScreensArray = Array.isArray(updatedScreen) ? updatedScreen : [updatedScreen];
    const updatedScreens = temporaryScreens.map(screen => {
      const matchingScreen = updatedScreensArray.find(
        updated => updated.source_element_id === screen.source_element_id
      );
      return matchingScreen
        ? {
            ...screen,
            ...matchingScreen,
            isModified: true
          }
        : screen;
    });
    setState(prevState => ({
      ...prevState,
      temporaryScreens: updatedScreens
    }));
    screensNotifier(updatedScreens);
  };

  const updateScreens = (
    updatedScreenFunction: (currentScreens: Partial<ModifiedScreen>[]) => Partial<ModifiedScreen>[]
  ) => {
    setState((prevState: State) => {
      const updatedScreens = updatedScreenFunction(prevState.temporaryScreens);
      screensNotifier(updatedScreens);
      return {
        ...prevState,
        temporaryScreens: updatedScreens
      };
    });
  };

  const cancelTemporaryScreen = () => {
    setState(prevState => ({
      ...prevState,
      temporaryScreen: null
    }));
  };

  const setTemporaryScreen = (newScreen: GeoDataAndScreensInteface, fly = false) => {
    if (map && fly) {
      map.flyTo({
        center: newScreen.polygon.coordinates,
        curve: 0.8,
        speed: 3,
        zoom: 16
      });
      map.once('zoomend', () => {
        setState(prevState => ({
          ...prevState,
          mode: null,
          temporaryScreen: newScreen
        }));
      });
    } else {
      setState(prevState => ({
        ...prevState,
        mode: null,
        temporaryScreen: newScreen
      }));
    }
  };

  const handleInventoryChange = async (inventory: {
    screenGroups?: ScreenGroupInterface[];
    screens?: (GeoDataAndScreensInteface & {
      toDelete?: boolean;
      toSave?: boolean;
      toUpdate?: boolean;
    })[];
    venueGroups?: string[];
    venues?: ModifiedVenue[];
  }) => {
    if (inventory.screenGroups && !isEqual(inventory.screenGroups, screenGroups)) {
      onChangeScreenGroups(inventory.screenGroups);
    }
    if (inventory.venueGroups?.length) {
      const knownVenueGroups = (contextData.user?.venueGroups ?? []).filter(g => g?._id);
      const reportedGroups = inventory.venueGroups
        .map(id => knownVenueGroups.find(g => String(g._id) === id))
        .filter((g): g is VenueGroupExtended => Boolean(g));
      if (reportedGroups.length) {
        setTemporaryVenueGroups(prev => {
          const merged = new Map(prev.map(g => [String(g._id), g]));
          reportedGroups.forEach(g => {
            merged.set(String(g._id), g);
          });
          return Array.from(merged.values());
        });
      }
    }
    if (inventory.screens?.[0]) {
      try {
        const newScreens = inventory.screens.map(({ toDelete, toSave, toUpdate, ...s }) => {
          const _id =
            temporaryScreens.find(ts => ts.source_element_id === s.source_element_id)?._id ||
            new Types.ObjectId().toString();
          return {
            ...s,
            _id,
            deleted: s.deleted || toDelete || false,
            isModified: Boolean(toSave || toUpdate || toDelete)
          };
        });
        setState(prevState => ({
          ...prevState,
          temporaryScreens: newScreens
        }));
        screensNotifier(newScreens);
      } catch {
        showNotification(
          'map',
          formatMessage({
            defaultMessage: 'An error occurred while synchronizing the screens.',
            id: 'multiFlightScreens.notifications.sync.error'
          })
        );
      }
    }

    if (inventory.venues?.[0]) {
      const hasTemps = temporaryVenues.length > 0;
      const base = hasTemps ? structuredClone(temporaryVenues) : [];
      const indexById: Record<string, number> = hasTemps
        ? base.reduce<Record<string, number>>((idx, v, i) => {
            if (v && v._id != null) {
              idx[String(v._id)] = i;
            }
            return idx;
          }, Object.create(null))
        : Object.create(null);

      const newTemporaryVenues = inventory.venues.reduce((acc, raw) => {
        const { _id, ...venue } = (raw ?? {}) as Venue;

        if (!hasTemps && venue?.deleted) {
          return acc;
        }

        const venueData: ModifiedVenue = {
          ...venue,
          _id,
          country: venue?.tags?.level0?.[0] ?? venue?.country ?? null,
          deleted: Boolean(venue?.deleted) || false,
          isModified: true,
          sourceType: venue?.sourceType ?? 'myVenues'
        };

        const key = String(_id);
        const idx = indexById[key];

        if (hasTemps && idx !== undefined) {
          acc[idx] = {
            ...acc[idx],
            ...venueData
          };
          return acc;
        }

        indexById[key] = acc.length;
        return [...acc, venueData];
      }, base);
      setState(prevState => ({
        ...prevState,
        temporaryVenues: newTemporaryVenues
      }));
      onVenuesChange(newTemporaryVenues as Venue[]);
    }
  };

  const addScreen = (screen: GeoDataAndScreensInteface) => {
    const newTemporaryScreens = [
      ...temporaryScreens,
      {
        ...screen,
        _id: new Types.ObjectId().toString(),
        groups: initialGroupIds('screens', screenGroups ?? []),
        isModified: true
      }
    ];
    setListAndState('screens', {
      temporaryScreen: null,
      temporaryScreens: newTemporaryScreens
    });
    screensNotifier(newTemporaryScreens);
  };

  const handleAddCircleZone = async ({ center, groups, name, radiusInKm, venueId }: CircleZone) => {
    const newTemporaryCircle = await generateZoneEntity({
      center,
      groups,
      name,
      radiusInKm,
      venueId
    });

    newItems.current.zones.add(newTemporaryCircle._id!);
    setListAndState('locations', prevState => {
      const newTemporaryZones = [...prevState.temporaryZones, newTemporaryCircle];
      zoneNotifier(newTemporaryZones as Zone[]);
      return {
        ...(prevState.mode === 'add-circles'
          ? {
              mode: null
            }
          : {}),
        temporaryZones: newTemporaryZones
      };
    });
  };

  const setAttributionZones = (attributionData: AttributionZones) => {
    setState(prevState => {
      Object.keys(attributionData).forEach(zoneId => {
        if (!prevState.attributionZones[zoneId]) {
          newItems.current.zones.add(zoneId);
        }
      });
      return {
        ...prevState,
        attributionZones: attributionData
      };
    });
  };

  // Enable attribution settings for the entity, but only if not already enabled.
  // `mode: 'footfall'` (used by the Proximity Points flow) enables footfall
  // attribution without `driveToStore`; the default `'driveToStore'` mode
  // preserves the existing Attribution POIs behaviour.
  const enableAttributionSettingsIfNeeded = (mode: AttributionEnableMode = 'driveToStore') => {
    const needsUpdate = needsAttributionSettingsUpdate(contextData.attributionSettings, mode);
    if (!needsUpdate) {
      return;
    }
    setAttributionSettings(
      mode === 'footfall'
        ? buildEnabledFootfallAttributionSettings(contextData.attributionSettings)
        : buildEnabledAttributionSettings(contextData.attributionSettings)
    );
  };

  const createAttributionZonesForVenues = (
    venuesForAttribution: Partial<Venue>[],
    attributionMode?: AttributionType,
    attributionRadiusInMeters?: number
  ) => {
    const updatedAttributionLocations = enableAttributionLocations(
      contextData.entityType,
      contextData.entityId,
      attributionZones,
      venuesForAttribution,
      attributionMode,
      attributionRadiusInMeters
    );
    setAttributionZones(updatedAttributionLocations as Record<string, ModifiedZone>);
  };

  const handleAddLocationsForVenueIds = async (ids: string[], radius: number = DEFAULT_RADIUS_IN_KM) => {
    if (!ids?.length) {
      return;
    }

    const idSet = new Set(ids);

    const venues = temporaryVenues.filter(
      v => idSet.has(v._id || '') && v.latlng?.lat !== undefined && v.latlng?.lng !== undefined
    );

    if (!venues.length) {
      return;
    }

    // Deleted zone groups are never reused: a new mirror group is created instead
    const zoneGroupsByVenueGroupId = new Map(
      zoneGroups.filter(z => z?.venueGroupId && isActiveGroup(z)).map(z => [z.venueGroupId, z])
    );

    const venueGroupById = new Map((contextData.user.venueGroups ?? []).filter(g => g?._id).map(g => [g._id, g]));

    const missingVenueGroupIds = new Set<string>();
    venues.forEach(v => {
      (v.groups ?? []).forEach(groupId => {
        if (!zoneGroupsByVenueGroupId.has(groupId)) {
          missingVenueGroupIds.add(groupId);
        }
      });
    });

    const newZoneGroupsByVenueGroupId = new Map();

    if (missingVenueGroupIds.size) {
      const now = new Date();
      const fallbackNameBase = getFormattedDateByUser(now, contextData.user, { withTime: true });

      missingVenueGroupIds.forEach(venueGroupId => {
        const vg = venueGroupById.get(venueGroupId);
        const newId = createGroupId();

        const zoneGroup = {
          _id: newId,
          attribution: false,
          color: generateRandomColor(true),
          name: vg?.name ?? `Group ${fallbackNameBase}`,
          smart: false,
          venueGroupId
        };

        newZoneGroupsByVenueGroupId.set(venueGroupId, zoneGroup);
      });

      onChangeZoneGroups([...zoneGroups, ...Array.from(newZoneGroupsByVenueGroupId.values())]);
    }

    const zonesToAdd: CircleZone[] = [];

    venues.forEach(v => {
      const venueGroupIds = v.groups ?? [];

      const resolvedZoneGroupIds = venueGroupIds.map(venueGroupId => {
        return (
          zoneGroupsByVenueGroupId.get(venueGroupId)?._id ??
          newZoneGroupsByVenueGroupId.get(venueGroupId)?._id ??
          venueGroupId
        );
      });

      zonesToAdd.push({
        address: v.address,
        center: [v.latlng!.lng, v.latlng!.lat] as [number, number],
        groups: resolvedZoneGroupIds,
        name: v.name,
        radiusInKm: radius,
        venueId: v._id
      });
    });

    const newTemporaryCircles: Partial<Zone>[] = await Promise.all(
      zonesToAdd.map(newZone =>
        generateZoneEntity({
          address: newZone.address,
          center: newZone.center,
          groups: newZone.groups,
          radiusInKm: newZone.radiusInKm,
          venueId: newZone.venueId,
          ...(newZone.name && { name: newZone.name })
        })
      )
    );

    const newTemporaryZones = [...temporaryZones, ...newTemporaryCircles];
    newTemporaryCircles.forEach(temporaryCircle => {
      newItems.current.zones.add(temporaryCircle._id!);
    });
    setListAndState('locations', () => {
      zoneNotifier(newTemporaryZones as Zone[]);
      return {
        mode: null,
        temporaryZones: newTemporaryZones
      };
    });
  };

  const handleAddBatchLocationsFromCSV = async (batchLocations: ProcessUploadZone[]) => {
    await onRefreshUser();
    // A CSV group name never matches a deleted group: a new group is created instead
    const groups = new Map(zoneGroups.filter(g => g?.name && isActiveGroup(g)).map(g => [g.name, g]));
    const newGroups = new Map();
    const locations = batchLocations
      .filter(location => !!location.status)
      .map(location => {
        const rawGroupName = typeof location.group === 'string' ? location.group.trim() : undefined;

        let group = rawGroupName ? groups.get(rawGroupName) : undefined;
        if (!group && rawGroupName) {
          group = {
            _id: createGroupId(),
            attribution: false,
            color: ItemsGroupsDefaultConfigurations.locations.color,
            name: rawGroupName,
            smart: false,
            sonataLiLocations: false
          };
          groups.set(rawGroupName, group);
          newGroups.set(rawGroupName, group);
        }

        if (location.type === 'circle') {
          const areaInSquareMeters = calculateCircleArea(location.radius!);
          const circleLocation: Partial<Zone> & { isModified: boolean } = {
            _id: new Types.ObjectId().toString(),
            area: Math.round(areaInSquareMeters),
            country: location.country,
            isModified: true,
            latlng: {
              lat: location.latlng!.lat,
              lng: location.latlng!.lng
            },
            name: location.name!,
            radius: location.radius!,
            type: 'circle',
            visible: location.visible
          };
          return {
            ...new ZoneEntity(circleLocation).entity,
            ...(group && group._id
              ? {
                  groups: [group._id]
                }
              : {})
          };
        }

        const cartographicLocation: Partial<Zone> & { isModified: boolean } = {
          _id: new Types.ObjectId().toString(),
          boundingBox: location.boundingBox,
          cartographyLevel: location.cartographyLevel,
          cartographyLevelId: location.cartographyLevelId,
          country: location.country,
          isModified: true,
          latlng: {
            lat: 0,
            lng: 0
          },
          name: location.name!,
          type: 'geojson',
          version: 2,
          visible: location.visible
        };
        return {
          ...new ZoneEntity(cartographicLocation).entity,
          ...(group && group._id
            ? {
                groups: [group._id]
              }
            : {})
        };
      });

    const newTemporaryZones = [...temporaryZones, ...locations];
    locations.forEach(location => {
      newItems.current.zones.add(location._id!);
    });
    setListAndState('locations', {
      mode: null,
      temporaryZones: newTemporaryZones
    });
    zoneNotifier(newTemporaryZones as Zone[]);
    onChangeZoneGroups([...zoneGroups, ...Array.from(newGroups.values())]);
  };

  const { cancelBatchUploadFile, processBatchUploadFile, processBatchUploadFileStatus } = useProcessCSVFile({
    entityId: contextData.entityId,
    entityType: contextData.entityType,
    onAddLocations: handleAddBatchLocationsFromCSV,
    onAddVenues: handleAddBatchVenues
  });

  const addLocationCircle = async (
    lat: number,
    lng: number,
    radiusInKm: number = DEFAULT_RADIUS_IN_KM,
    name?: string
  ) => {
    await handleAddCircleZone({
      center: [lng, lat],
      ...(name ? { name } : {}),
      radiusInKm
    });
    setListAndState('locations', {
      mode: null
    });
  };

  const addPolygon = async (coordinates: [number, number][], nameOverride?: string) => {
    if (coordinates.length < 3) {
      return;
    }

    const closedCoords = [...coordinates, coordinates[0]];

    const polygon = {
      coordinates: [closedCoords],
      type: 'Polygon'
    };
    const polygonArea = Math.round(
      turf.area({
        geometry: {
          coordinates: [closedCoords],
          type: 'Polygon'
        },
        properties: {},
        type: 'Feature'
      })
    );

    const geojson: Zone['geojson'] = {
      geometry: polygon,
      type: 'Feature'
    };

    const firstCoord = coordinates[0];
    const info: any = await getInfoFromMapBox(firstCoord[1], firstCoord[0]);
    const country = normalizeCountryToCca3(info.features.find(findCountryFeature)?.properties?.short_code) || 'N/A';

    const zone: Partial<ModifiedZone> = {
      _id: new Types.ObjectId().toString(),
      area: polygonArea,
      country,
      geojson,
      isModified: true,
      latlng: {
        lat: 0,
        lng: 0
      },
      ...(nameOverride ? { name: nameOverride } : {}),
      type: 'polygon'
    };

    const zoneEntity = new ZoneEntity(zone);

    const newTemporaryShape = structuredClone(zoneEntity.entity);
    newItems.current.zones.add(newTemporaryShape._id!);
    setListAndState('locations', prevState => ({
      ...(prevState.mode === 'draw-polygons'
        ? {
            mode: null
          }
        : {}),
      temporaryZones: [...prevState.temporaryZones, newTemporaryShape]
    }));
    zoneNotifier([...temporaryZones, newTemporaryShape] as Zone[]);
  };

  const clearTemporaryLocation = () => {
    setState((prevState: State) => ({
      ...prevState,
      temporaryLocation: null
    }));
  };

  const confirmAddLocation = async () => {
    setListAndState('locations', (prevState: State) => {
      zoneNotifier([...prevState.temporaryZones, prevState.temporaryLocation] as Zone[]);
      return {
        temporaryLocation: null,
        ...(prevState.temporaryLocation && {
          temporaryZones: [...prevState.temporaryZones, prevState.temporaryLocation as Partial<Zone>]
        })
      };
    });
  };

  const handleSaveCartographicLocations = (zones: Partial<Zone>[]) => {
    const zoneEntities = zones.map(zone => new ZoneEntity(zone).entity) as Partial<Zone>[];
    zoneEntities.forEach(zoneEntity => {
      newItems.current.zones.add(zoneEntity._id!);
    });
    setListAndState('locations', (prevState: State) => {
      zoneNotifier([...prevState.temporaryZones, ...zoneEntities] as Zone[]);
      return {
        temporaryLocation: null,
        temporaryZones: [...prevState.temporaryZones, ...zoneEntities]
      };
    });
  };

  const updateZone = async (updatedZone: Partial<ModifiedZone> | Partial<ModifiedZone>[], shouldNotify = true) => {
    const updatedZonesArray = Array.isArray(updatedZone) ? updatedZone : [updatedZone];
    const applyUpdates = (zones: Partial<ModifiedZone>[]) =>
      zones.map(zone => {
        const matchingZone = updatedZonesArray.find(updated => updated._id === zone._id);
        return matchingZone
          ? {
              ...zone,
              ...matchingZone,
              isModified: true
            }
          : zone;
      });
    // Same as addPolygon: the state update stays functional so updates queued in the same tick
    // compose, but the notified list can't come from the updater — React may run it after this call.
    setState(prevState => ({
      ...prevState,
      temporaryZones: applyUpdates(prevState.temporaryZones)
    }));
    if (shouldNotify) {
      zoneNotifier(applyUpdates(temporaryZones) as Zone[]);
    }
  };

  const updateCircle = async (updatedZone: Partial<ModifiedZone>) => {
    updateZone(updatedZone, false);
    if (updatedZone.latlng?.lat && updatedZone.latlng.lng) {
      const info: any = await getInfoFromMapBox(updatedZone.latlng.lat, updatedZone.latlng?.lng);
      const countryFeature = info.features?.find(findCountryFeature);
      const country = normalizeCountryToCca3(countryFeature?.properties?.short_code) || 'N/A';
      updatedZone.country = country;
      updateZone(updatedZone);
      const upadtedConcentricZones = torusZones
        .filter(zone => updatedZone.parentTorus === zone.parentTorus)
        .map(zone => ({
          ...zone,
          country,
          latlng: updatedZone.latlng
        }));
      updateZone([updatedZone, ...upadtedConcentricZones]);
    }
  };

  const updatePolygon = (id: string, coords: [number, number][]) => {
    const zoneToUpdate = temporaryZones?.find((shape: Partial<Zone>) => shape._id === id);
    if (zoneToUpdate) {
      const polygon = {
        coordinates: [coords],
        type: 'Polygon'
      };
      const polygonArea = Math.round(
        turf.area({
          geometry: {
            coordinates: [coords],
            type: 'Polygon'
          },
          properties: {},
          type: 'Feature'
        })
      );
      const geojson: Zone['geojson'] = {
        geometry: polygon,
        type: 'Feature'
      };
      const sanitizedZone = sanitizeObj(zoneToUpdate) as Zone;
      updateZone({
        ...sanitizedZone,
        area: polygonArea,
        geojson,
        isModified: true
      });
    }
  };

  const getItemsToUpdate = <T extends ModifiedZone | ModifiedScreen | ModifiedVenue>(
    itemType: ListType,
    ids: string[]
  ) => {
    let items: T[] = [];
    switch (itemType) {
      case 'locations':
        items = temporaryZones as T[];
        return items.filter(item => ids.includes(item._id!));
      case 'screens':
        items = temporaryScreens as T[];
        return items.filter(item => ids.includes(String((item as ModifiedScreen).source_element_id!)));
      case 'venues':
        items = temporaryVenues as T[];
        return items.filter(item => ids.includes(item._id!));
      default:
        throw new Error(`Unknown item type: ${itemType}`);
    }
  };

  const getUpdateFunction = (itemType: ListType) => {
    switch (itemType) {
      case 'locations':
        return updateZone;
      case 'screens':
        return updateScreen;
      case 'venues':
        return updateVenue;
      default:
        throw new Error(`Unknown item type: ${itemType}`);
    }
  };

  const disableMultipleItems = (
    itemType: ListType,
    itemsToUpdate: Array<ModifiedZone | ModifiedScreen | ModifiedVenue>
  ) => {
    const updater = getUpdateFunction(itemType);
    const updatedItems = itemsToUpdate.map(item => ({
      ...item,
      isModified: true,
      visible: false
    }));
    updater(updatedItems);
  };

  const enableMultipleItems = (
    itemType: ListType,
    itemsToUpdate: Array<ModifiedZone | ModifiedScreen | ModifiedVenue>
  ) => {
    const updater = getUpdateFunction(itemType);
    const updatedItems = itemsToUpdate.map(item => ({
      ...item,
      isModified: true,
      visible: true
    }));
    updater(updatedItems);
  };

  const setLocationSmart = (newSmartValue: boolean) => {
    const itemType = 'locations';
    const updater = getUpdateFunction(itemType);
    const itemsToUpdate = getItemsToUpdate<ModifiedZone>(itemType, selectedItems[itemType]);

    let shouldShowNotification = false;

    const allowedItems = itemsToUpdate.filter(item => {
      if (!contextData.entityIsDraft && item._id && !newItems.current.zones.has(item._id!)) {
        shouldShowNotification = true;
        return false;
      }
      return true;
    });

    updater(
      allowedItems.map(item => ({
        ...item,
        smart: newSmartValue
      }))
    );

    if (shouldShowNotification) {
      showNotification(
        'map',
        formatMessage({
          defaultMessage: `The smart status of locations in published campaigns can't be changed,
         except for temporary locations still in draft`,
          id: 'multiFlightLocations.messages.publishedCampaignLocationSmartUnchangeable'
        })
      );
    }
  };

  const deleteMultipleItems = (itemType: ListType, ids: string[]) => {
    const updater = getUpdateFunction(itemType);
    switch (itemType) {
      case 'locations': {
        const itemsToUpdate = getItemsToUpdate<ModifiedZone>(itemType, ids);
        // Only flag the zones: their group memberships may have just been cleaned in the same action
        updater(itemsToUpdate.map(({ _id }) => ({ _id, deleted: true, isModified: true })));
        if (itemsToUpdate.length > 0) {
          showNotification(
            'success',
            formatMessage(
              {
                defaultMessage:
                  '{count, plural, one {Activation area deleted} other {{count} activation areas deleted}}',
                id: 'multiFlightListsMap.actionButtons.notification.locationDeleted'
              },
              { count: itemsToUpdate.length }
            )
          );
        }
        break;
      }
      case 'screens': {
        const itemsToUpdate = getItemsToUpdate<ModifiedScreen>(itemType, ids);
        // updateScreens (unlike updateZone/updateVenue) takes an updater callback, not an array —
        // `updater` from getUpdateFunction can't be used here directly for that reason.
        updateScreens(screens =>
          screens.map(screen =>
            itemsToUpdate.some(item => item.source_element_id === screen.source_element_id)
              ? { ...screen, deleted: true, isModified: true }
              : screen
          )
        );
        if (itemsToUpdate.length > 0) {
          showNotification(
            'success',
            formatMessage(
              {
                defaultMessage: '{count, plural, one {Screen deleted} other {{count} screens deleted}}',
                id: 'multiFlightListsMap.actionButtons.notification.screenDeleted'
              },
              { count: itemsToUpdate.length }
            )
          );
        }
        break;
      }
      case 'venues': {
        const itemsToUpdate = getItemsToUpdate<ModifiedVenue>(itemType, ids);
        const venueIdsToDelete = new Set(itemsToUpdate.map(venue => venue._id!));
        const relationKey = getVenueEntityRelationKey(contextData.entityType);

        setState(prevState => {
          const newTemporaryVenues = prevState.temporaryVenues.map(venue => {
            const isMatching = venue._id && venueIdsToDelete.has(venue._id);
            if (!isMatching) {
              return venue;
            }
            const relations = (venue[relationKey] as string[] | undefined) ?? [];
            return {
              ...venue,
              [relationKey]: relations.filter(id => id !== contextData.entityId),
              deleted: true,
              isModified: true
            };
          });
          onVenuesChange(newTemporaryVenues as Venue<string>[]);

          // No venues left on the entity (Attribution POIs or Proximity
          // Points alike) — reset attribution settings, same shape as
          // `handleDisableAttribution` in ListActionButtons.
          const hasNoLiveVenues = newTemporaryVenues.every(venue => venue.deleted);
          if (hasNoLiveVenues && contextData.attributionSettings?.enabled) {
            setAttributionSettings({
              ...contextData.attributionSettings,
              driveToStore: { enabled: false },
              enabled: false,
              settings: { traffic: false, visits: false }
            });
          }

          return {
            ...prevState,
            attributionZones: Object.entries(prevState.attributionZones).reduce(
              (acc, [zoneId, zone]) => ({
                ...acc,
                [zoneId]: venueIdsToDelete.has(zone.shop as string)
                  ? {
                      ...zone,
                      deleted: true,
                      isModified: true,
                      // The map renderer (CirclesShapesLayer) decides visibility from
                      // torusConfig[].deleted, not the zone's own `deleted` flag —
                      // mirror what disableAttributionLocations does, or the torus
                      // keeps rendering after the venue itself is gone.
                      torusConfig: zone.torusConfig?.map(config => ({ ...config, deleted: true }))
                    }
                  : zone
              }),
              {} as AttributionZones
            ),
            temporaryVenues: newTemporaryVenues,
            temporaryZones: prevState.temporaryZones.map(zone => {
              const isAssociatedWithDeletedVenue = zone.shop && venueIdsToDelete.has(zone.shop);
              return isAssociatedWithDeletedVenue
                ? {
                    ...zone,
                    deleted: true,
                    isModified: true
                  }
                : zone;
            })
          };
        });
        if (itemsToUpdate.length > 0) {
          showNotification(
            'success',
            formatMessage(
              {
                defaultMessage:
                  '{isProximityPoint, select, true {{count, plural, one {Proximity Point deleted} other {{count} Proximity Points deleted}}} other {{count, plural, one {Attribution POI deleted} other {{count} Attribution POIs deleted}}}}',
                id: 'multiFlightListsMap.actionButtons.notification.venueDeleted'
              },
              { count: itemsToUpdate.length, isProximityPoint: contextData.activeSubsection === 'proximityPoints' }
            )
          );
        }
        break;
      }
      default:
        break;
    }
  };

  const setPrefixSuffix = (prefix: string, suffix: string) => {
    const itemType = 'locations';
    const updater = getUpdateFunction(itemType);
    const itemsToUpdate = getItemsToUpdate<ModifiedZone>(itemType, selectedItems[itemType]);
    updater(
      itemsToUpdate.map(item => ({
        ...item,
        prefix,
        suffix
      }))
    );
  };

  const setAttributionSettings = (settings: AttributionSettings) => {
    contextData.onChangeAttributionSettings(settings);
  };

  const addItemsFromSonataLI = ({
    concentricZonesRadius = [],
    ignoreVenues = false,
    venues = [],
    zoneGroups: newZoneGroups = [],
    zones = []
  }: ExportFromSonataLIData) => {
    const newVenues = ignoreVenues
      ? []
      : (venues.map(venue => ({
          ...(contextData.entityType === 'lineItem'
            ? { campaigns: [contextData.entityId] as any }
            : { insertionOrders: [contextData.entityId] as any }),
          ...(venue.geoDataId &&
            venue.geoDataId !== '' &&
            venue.geoDataSourceId &&
            venue.geoDataSourceId !== '' && {
              geoDataId: venue.geoDataId,
              geoDataSourceId: venue.geoDataSourceId
            }),
          _id: new Types.ObjectId().toString(),
          country: venue.country,
          deleted: false,
          groups: venue.groups,
          isModified: true,
          latlng: {
            lat: venue?.latlng!.lat,
            lng: venue?.latlng!.lng
          },
          name: venue.name,
          sourceType: venue.source?.includes('poibrand') ? 'poiBrands' : ((venue.source as SourceType) ?? 'poi'),
          user: contextData.user._id || (contextData.user.id as any)
        })) as ModifiedVenue[]);

    const commonZoneAttributes: Partial<ModifiedZone> = {
      [contextData.entityType === 'lineItem' ? 'campaign' : 'insertionOrder']: contextData.entityId,
      deleted: false,
      isModified: true,
      sonataLI: true,
      visible: true
    };

    const newGroups: ZoneGroupInterface<string>[] = newZoneGroups
      .filter(g => g.ids.length)
      .map(group => ({
        _id: group._id,
        attribution: false,
        color: group.color,
        name: group.name,
        smart: false,
        sonataLiLocations: true
      }));

    const newVenueGroups: VenueGroupExtended[] = newZoneGroups
      .filter(g => g.ids.length)
      .map(group => ({
        _id: group._id,
        color: group.color,
        groupType: group.groupType,
        icon: group.icon,
        name: group.name
      }));

    const newZones = zones.map(zone => {
      const geojson: Zone['geojson'] | null = zone.polygon
        ? ({
            geometry: zone.polygon,
            type: 'Feature'
          } as Zone['geojson'])
        : null;
      const polygonArea = zone.polygon ? Math.round(turf.area(geojson as Feature)) : 0;

      return {
        ...commonZoneAttributes,
        _id: new Types.ObjectId().toString(),
        ...(zone.polygon
          ? {
              area: polygonArea,
              geojson
            }
          : {
              cartographyLevel: zone.cartographyLevel!,
              cartographyLevelId: zone.cartographyLevelId!,
              version: 2
            }),
        country: zone.country,
        groups: zone.groups,
        name: zone.name,
        sourceIndexes: [zone.sonataLIProps.layerId as any],
        type: zone.polygon ? 'polygon' : 'geojson'
      };
    }) as ModifiedZone[];

    let concentricZones: Partial<ModifiedZone>[] = [];
    if (concentricZonesRadius.length > 0 && venues.length > 0) {
      const sortedRadiusList = [...concentricZonesRadius].sort((a, b) => a - b);
      concentricZones = venues.flatMap((venue, venueIndex) => {
        const parentTorus = new Types.ObjectId().toString();
        return sortedRadiusList.map((radius, index) => ({
          ...commonZoneAttributes,
          _id: new Types.ObjectId().toString(),
          area: calculateCircleArea(radius),
          country: venue.country,
          groups: venue.groups,
          latlng: { ...(venue.latlng as any) },
          maxRadius: sortedRadiusList[index + 1],
          minRadius: sortedRadiusList[index - 1] || 1,
          name: `${venue.name} - ${radius}m`,
          parentTorus,
          radius,
          ...(!ignoreVenues && {
            shop: newVenues[venueIndex]._id
          }),
          sourceIndexes: [venue.sonataLIProps!.layerId as any],
          type: 'torus'
        }));
      });
    }
    const mergedVenues = [...temporaryVenues, ...newVenues];
    const mergedZones = [...temporaryZones, ...newZones, ...concentricZones];
    newZones.forEach(zone => {
      newItems.current.zones.add(zone._id!);
    });
    concentricZones.forEach(zone => {
      newItems.current.zones.add(zone._id!);
    });
    newVenues.forEach(venue => {
      newItems.current.venues.add(venue._id!);
    });

    setListAndState(newVenues.length > newZones.length + concentricZones.length ? 'venues' : 'locations', {
      mode: null,
      temporaryLocation: null,
      temporaryVenues: mergedVenues,
      temporaryZones: mergedZones
    });
    newVenues.length && onVenuesChange(newVenues as Venue<string>[]);
    zoneNotifier(mergedZones as Zone[]);
    newGroups.length &&
      (newZones.length || concentricZones.length) &&
      onChangeZoneGroups([...zoneGroups, ...newGroups]);
    newVenueGroups.length &&
      newVenues.length &&
      setTemporaryVenueGroups(prev => {
        const filtered = prev.filter(g => !newVenueGroups.some(ng => ng._id === g._id));
        return [...filtered, ...newVenueGroups];
      });
  };

  const setConcentricLocations = (itemIds: string[], radiusList: number[]): void => {
    const sortedRadiusList = radiusList.sort((a, b) => a - b);
    setState((prevState: State) => ({
      ...prevState,
      temporaryZones: prevState.temporaryZones.reduce((prev, current) => {
        const parentTorus = new Types.ObjectId().toString();
        const newConcentricZones = sortedRadiusList.map(
          (radius, index) =>
            ({
              ...current,
              isModified: true,
              maxRadius: sortedRadiusList[index + 1],
              minRadius: sortedRadiusList[index - 1] || 1,
              name: `${current.name} - ${index + 1}`,
              parentTorus,
              radius,
              type: 'torus',
              ...(index > 0 && {
                _id: new Types.ObjectId().toString()
              })
            }) as Partial<ModifiedZone>
        );
        newConcentricZones.forEach(zone => {
          newItems.current.zones.add(zone._id!);
        });

        return [...prev, ...(current._id && itemIds.includes(current._id) ? newConcentricZones : [current])];
      }, [] as Partial<ModifiedZone>[])
    }));
  };

  const addTemporaryCartographicLocation = (country: string, shape: ExtendedShape) => {
    const cartographyLevelId = shape.name;

    const zone = {
      _id: new Types.ObjectId().toString(),
      boundingBox: shape.boundingBox,
      cartographyLevel: shape.shape,
      cartographyLevelId,
      country,
      isModified: true,
      latlng: {
        lat: 0,
        lng: 0
      },
      name: shape.description,
      type: 'geojson',
      version: 2
    };

    const entityZone = new ZoneEntity(zone);

    if (allLocations.some(location => location.cartographyLevelId === cartographyLevelId)) {
      return;
    }

    newItems.current.zones.add(entityZone.entity._id!);
    setState((prevState: State) => ({
      ...prevState,
      temporaryLocation: entityZone.entity
    }));
  };

  const saveVenues = async () => {
    const venuesToAdd: string[] = [];
    const temporaryVenueIdMap: Record<string, string> = {};
    const venuesToRemove = venueIdsToRemove;

    // Venues losing their association with this entity are excluded from the
    // `updateVenues` payload entirely — that endpoint has no entityId/entityType
    // and would otherwise `$set` the whole document (including a stale `deleted`
    // flag), clobbering the atomic `$pull` that `syncRelationsVenues` performs.
    const venuesToRemoveSet = new Set(venuesToRemove);
    const modifiedVenuesToUpdate = temporaryVenues
      .filter(
        venue => venue.isModified && !newItems.current.venues.has(venue._id!) && !venuesToRemoveSet.has(venue._id!)
      )
      .map(venue => {
        const { isModified, ...venueToUpdate } = venue;
        return venueToUpdate;
      });

    const newVenuesToCreate = temporaryVenues.filter(
      venue => newItems.current.venues.has(venue._id!) && !venue.deleted
    );

    let newVenues: Venue[] = [];
    try {
      if (newVenuesToCreate.length > 0) {
        const createdVenues = await postAddVenues(newVenuesToCreate as Venue[], tokens.venuesBatchSave);
        newVenues = createdVenues.map((createdVenue, index) => {
          const newVenue = structuredClone(newVenuesToCreate[index]);
          if (createdVenue._id && newVenue._id) {
            venuesToAdd.push(createdVenue._id.toString());
            temporaryVenueIdMap[newVenue._id.toString()] = createdVenue._id.toString();
          }
          return createdVenue;
        });
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(`Error creating ${newVenuesToCreate.length} venues`, error);
      throw error;
    }

    const updateVenuePromise =
      modifiedVenuesToUpdate.length > 0 ? updateVenues(modifiedVenuesToUpdate) : Promise.resolve([]);

    await Promise.all([...newVenues, updateVenuePromise]);

    const syncRelationsPayload = {
      add: venuesToAdd,
      entityId: contextData.entityId,
      entityType: contextData.entityType,
      remove: venuesToRemove
    };

    if (syncRelationsPayload.add.length > 0 || syncRelationsPayload.remove.length > 0) {
      await syncRelationsVenues(syncRelationsPayload);
    }

    return temporaryVenueIdMap;
  };

  /**
   * Persist any venue groups created, edited or soft-deleted (`deleted: true`)
   * in this session by writing the merged user back via `putUser`. Groups
   * created and hard-deleted in this session are not in the buffer, so they
   * are never sent. Runs before `saveVenues` so the venue update API never
   * references a `groups` id we haven't persisted yet.
   */
  const saveVenueGroups = async () => {
    if (temporaryVenueGroups.length === 0) {
      return;
    }
    // The canonical user object stores ObjectIds; this provider only works with the string-id flavour.
    const persistedGroups = (contextData.user?.venueGroups ?? []) as unknown as VenueGroupExtended[];
    const { changed, groups: updatedVenueGroups } = mergeVenueGroupsForSave(persistedGroups, temporaryVenueGroups);
    if (!changed) {
      return;
    }

    const userToPersist = {
      ...contextData.user,
      venueGroups: updatedVenueGroups as (typeof contextData.user)['venueGroups']
    };
    await putUser(userToPersist, {});
    reloadUser({});
    // Refresh the user-scoped query caches (venues are scoped by user._id),
    // so any consumer reading `user.venueGroups` sees the new entries.
    await onRefreshUser();
  };

  /**
   * Refetching these three is also what clears every `isModified` flag: each
   * query fn rebuilds its slice of state from the server response, where the
   * flag is `false` or absent, which is what lets `hasUnsavedMapChanges` settle
   * back to false after a save.
   *
   * `multiFlightMap-entities` is `enabled` only while `temporaryZones` is empty,
   * so by this point its own guard is false. react-query v3 runs it anyway —
   * `refetchQueries` fetches every match with no `enabled`/active check — and
   * that is load-bearing here. If an upgrade stops forcing disabled queries,
   * the unsaved-changes state stops resetting after a save (Save stays enabled,
   * the navigation guard keeps firing) for venues, zones and screens alike.
   */
  const handleInvalidateMapCache = async () => {
    await Promise.all([
      queryClient.refetchQueries(['multiFlightMap-entities', contextData.entityId, contextData.entityType], {
        exact: true
      }),
      queryClient.refetchQueries(
        ['multiFlightMap_screens', contextData.entityId, contextData.entityType, contextData.user._id],
        { exact: true }
      ),
      queryClient.refetchQueries(
        ['multiFlightMap-venues', contextData.entityId, contextData.entityType, contextData.user._id],
        { exact: true }
      )
    ]);
  };

  const onSave = async () => {
    try {
      if (isSaving) {
        return;
      }

      setState((prevState: State) => ({
        ...prevState,
        isSaving: true
      }));

      // Declare both map steps up front so the tooltip shows the full list
      // from the start, then only ever move the ones that have work to do.
      contextData.onSaveStepChange?.('venues', hasUnsavedVenues ? 'pending' : 'disabled', unsavedVenuesCounts);
      contextData.onSaveStepChange?.(
        'attributionZones',
        hasUnsavedAttributionZones ? 'pending' : 'disabled',
        unsavedAttributionZonesCounts
      );
      contextData.onSaveStepChange?.('screens', hasUnsavedAreas ? 'pending' : 'disabled', unsavedAreasCounts);

      if (hasUnsavedVenues) {
        contextData.onSaveStepChange?.('venues', 'processing');
      }
      let temporaryVenueIdMap: Record<string, string>;
      try {
        // Persist new/edited venue groups BEFORE saving the venues themselves
        // so the venues' `groups` ids (which can include freshly-minted
        // venue-group ids that only existed in `temporaryVenueGroups`) point
        // at rows that already exist on the user. After this call returns,
        // `temporaryVenueGroups` becomes redundant — `user.venueGroups`
        // carries the canonical list.
        await saveVenueGroups();

        temporaryVenueIdMap = await saveVenues();
        if (hasUnsavedVenues) {
          contextData.onSaveStepChange?.('venues', 'success');
        }
      } catch (error) {
        // Reported even for a step nobody expected to run: a silent failure
        // here would let the entity save proceed on top of it.
        contextData.onSaveStepChange?.('venues', 'fail');
        throw error;
      }

      const attributionZonesArray = Object.values(attributionZones);

      const updatedZonesAfterVenueSave = [...temporaryZones, ...attributionZonesArray].map(zone => {
        if (newItems.current.venues.has(zone.shop!)) {
          const definitiveVenueId = temporaryVenueIdMap[zone.shop!];
          if (definitiveVenueId) {
            return {
              ...zone,
              ...(zone.type !== 'attributionTorus' ? { isModified: true } : {}),
              shop: definitiveVenueId
            };
          }
        }
        return zone;
      });

      setState(prevState => ({
        ...prevState,
        attributionZones: getAttributionZonesObject(updatedZonesAfterVenueSave),
        temporaryZones: updatedZonesAfterVenueSave.filter(({ type }) => type !== 'attributionTorus')
      }));

      const modifiedZonesToUpdate = updatedZonesAfterVenueSave
        .filter(zone => zone.isModified && !newItems.current.zones.has(zone._id!))
        .map(zone => {
          const { isModified, ...zoneToUpdate } = zone;
          return zoneToUpdate;
        });

      const newZonesToCreate = updatedZonesAfterVenueSave.filter(
        zone => newItems.current.zones.has(zone._id!) && !zone.deleted
      );

      const updateZonesPromise =
        modifiedZonesToUpdate.length > 0
          ? updateEntityZones(contextData.entityType, contextData.entityId, modifiedZonesToUpdate as Zone[])
          : Promise.resolve([]);

      const createNewZonesPromise =
        newZonesToCreate.length > 0
          ? createEntityZones(contextData.entityType, contextData.entityId, newZonesToCreate as Zone[])
          : Promise.resolve([]);

      const modifiedScreens = temporaryScreens.filter(screen => screen.isModified);

      const createScreensPromise =
        modifiedScreens.length > 0
          ? saveScreens(
              contextData.entityType,
              contextData.entityId,
              modifiedScreens.map(screen => ({
                ...(screen._id && {
                  _id: screen._id
                }),
                audienceRadius: screen.audienceRadius || 250,
                deleted: screen.deleted || false,
                deviceId: screen.device_id,
                groups: screen.groups || [],
                removed: screen.removed || false,
                sourceElementId: String(screen.source_element_id),
                visible: screen.visible || true,
                ...(contextData.entityType === 'lineItem'
                  ? { campaign: contextData.entityId }
                  : { insertionOrder: contextData.entityId })
              }))
            )
          : Promise.resolve([]);

      // Attribution rings and activation areas share these create/update
      // calls, so their two steps move together — but each only moves if it
      // actually put something in the batch.
      if (hasUnsavedAttributionZones) {
        contextData.onSaveStepChange?.('attributionZones', 'processing');
      }
      if (hasUnsavedAreas) {
        contextData.onSaveStepChange?.('screens', 'processing');
      }
      try {
        await Promise.all([createNewZonesPromise, createScreensPromise, updateZonesPromise]);
        if (hasUnsavedAttributionZones) {
          contextData.onSaveStepChange?.('attributionZones', 'success');
        }
        if (hasUnsavedAreas) {
          contextData.onSaveStepChange?.('screens', 'success');
        }
      } catch (error) {
        if (hasUnsavedAttributionZones) {
          contextData.onSaveStepChange?.('attributionZones', 'fail');
        }
        // The areas step doubles as the fallback when neither step declared
        // itself: a failure here must never be silent, or the entity save
        // proceeds on top of it.
        if (hasUnsavedAreas || !hasUnsavedAttributionZones) {
          contextData.onSaveStepChange?.('screens', 'fail');
        }
        throw error;
      }
      await handleInvalidateMapCache();
      setGroupsExistingOnLoad({
        locations: collectGroupIds(zoneGroups),
        screens: collectGroupIds(screenGroups, temporaryScreenGroups),
        venues: collectGroupIds(contextData.user?.venueGroups, temporaryVenueGroups)
      });
    } catch (error: any) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error('Error saving map data:', error);
    } finally {
      newItems.current = {
        venues: new Set(),
        zones: new Set()
      };
      setState((prevState: State) => ({
        ...prevState,
        isSaving: false,
        // Drop the temporary group buffers now that the canonical sources
        // (user.venueGroups via putUser, entity.screenGroups via the
        // parent's save flow) carry the changes forward. Subsequent edits
        // start from a clean slate against the just-refreshed canonical
        // lists.
        temporaryScreenGroups: [],
        temporaryVenueGroups: []
      }));
      contextData.onSaveComplete();
    }
  };

  if (contextData.shouldMapSave && !isSaving) {
    onSave();
  }

  /**
   * Merged venueGroups view used by the grouping UI (and any other
   * consumer that needs the union of persisted + in-flight groups).
   *
   * - Start from `contextData.user.venueGroups` (canonical, persisted).
   * - Override / append entries from `temporaryVenueGroups` so:
   *     · brand-new groups (not yet in `user.venueGroups`) appear, and
   *     · edits-in-flight beat the canonical copy in the merged list.
   *
   * Identity is by `_id` (stringified). All ids are normalized to strings
   * because the rest of the provider works exclusively with string ids.
   */
  const venueGroups: VenueGroupExtended[] = useMemo(() => {
    const merged = new Map<string, VenueGroupExtended>();
    (contextData.user?.venueGroups ?? []).forEach(g => {
      const id = g._id ? String(g._id) : '';
      if (id) {
        merged.set(id, { ...(g as unknown as VenueGroupExtended), _id: id });
      }
    });
    temporaryVenueGroups.forEach(tg => {
      const id = tg._id ? String(tg._id) : '';
      if (id) {
        merged.set(id, { ...tg, _id: id });
      }
    });
    return Array.from(merged.values());
  }, [contextData.user?.venueGroups, temporaryVenueGroups]);

  /**
   * Merged screenGroups view used by the grouping UI for screens.
   *
   * - Start from the entity-scoped `screenGroups` provider prop (canonical
   *   for this entity; can contain system-managed `'index'` /
   *   `'cartography'` groups alongside user-created `'custom'` ones).
   * - Override / append entries from `temporaryScreenGroups` (always
   *   `groupType: 'custom'`) — brand-new ones appear, in-flight edits to
   *   custom-type entries win over their canonical counterpart.
   *
   * Identity is by stringified `_id`, matching the venue-groups merge.
   */
  const mergedScreenGroups: ScreenGroupInterface[] = useMemo(() => {
    const merged = new Map<string, ScreenGroupInterface>();
    (screenGroups ?? []).forEach(g => {
      const id = g._id ? String(g._id) : '';
      if (id) {
        merged.set(id, { ...g, _id: id });
      }
    });
    temporaryScreenGroups.forEach(tg => {
      const id = tg._id ? String(tg._id) : '';
      if (id) {
        merged.set(id, { ...tg, _id: id });
      }
    });
    return Array.from(merged.values());
  }, [screenGroups, temporaryScreenGroups]);

  return (
    <MultiFlightMapContext.Provider
      value={{
        ...contextData,
        addItemsFromSonataLI,
        addLocationCircle,
        addPolygon,
        addScreen,
        addSourcesAsScreens,
        addSourcesAsVenues,
        addTemporaryCartographicLocation,
        addVenue,
        attributionSettings: contextData.attributionSettings,
        attributionZones,
        cancelBatchUploadFile,
        cancelTemporaryScreen,
        centerElementsOnTheMap,
        circleZones,
        clearTemporaryItems,
        clearTemporaryLocation,
        confirmAddLocation,
        createAttributionZonesForVenues,
        currentSection,
        cursorPosition,
        deleteMultipleItems,
        disableMultipleItems,
        editedCircleId,
        editedPolygonId,
        enableMultipleItems,
        entityId: contextData.entityId,
        entityIsDraft: contextData.entityIsDraft,
        entityType: contextData.entityType,
        fullShapes: temporaryZones,
        groupsExistingOnLoad,
        isSaving,
        list,
        loading: isLoadingVenues || countryDataFetching || countryDataLoading || isLoadingShapes || isLoadingScreens,
        loadingLocationList: isLoadingShapes,
        loadingScreensList: isLoadingScreens,
        loadingSourcesList: isLoadingSources,
        loadingVenuesList: isLoadingVenues,
        locations: allLocations,
        mode,
        onAddBatchVenues: handleAddBatchVenues,
        onAddCircleZone: handleAddCircleZone,
        onAddLocationsForVenueIds: handleAddLocationsForVenueIds,
        onChangeZoneGroups,
        onInventoryChange: handleInventoryChange,
        onSave,
        onSaveCartographicLocations: handleSaveCartographicLocations,
        processBatchUploadFile,
        processBatchUploadFileStatus,
        screenGroups: mergedScreenGroups,
        screens: screensData || [],
        selectedItemId,
        selectedItems,
        setAttributionSettings,
        setAttributionZones,
        setConcentricLocations,
        setEditedCircleId,
        setEditedPolygonId,
        setList,
        setLocationSmart,
        setMode,
        setPrefixSuffix,
        setSelectedItemId,
        setSelectedItems,
        setTemporaryScreen,
        setTemporaryScreenGroups,
        setTemporaryVenueGroups,
        shapes: allShapes,
        sources: isLoadingSources ? { allSources: [] } : sourcesByCountry,
        targeting: contextData.targeting,
        temporaryLocation,
        temporaryScreen,
        temporaryScreens,
        temporaryVenues,
        temporaryZones,
        torusZones,
        updateCircle,
        updatePolygon,
        updateScreens,
        updateVenue,
        updateZone,
        user: normalizedUser,
        userRoles: contextData.userRoles,
        venueGroups,
        venues: venuesData || [],
        venuesSourceData: contextData?.venuesSourceData || [],
        zoneGroups
      }}
    >
      {children}
    </MultiFlightMapContext.Provider>
  );
}

function useMultiFlightMap(): Context {
  const context = useContext(MultiFlightMapContext);
  if (!context) {
    throw new Error('useMultiFlightMap cannot be use it outside the MultiFlightMapProvider component');
  }
  return context;
}

export { MultiFlightMapProvider, useMultiFlightMap };
